import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { ShieldCheck, Globe, Sun, Moon, Loader2, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const { t, login, language, setLanguage, theme, toggleTheme } = useApp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password, remember);
      toast.success(`${t("welcomeBack")}, ${u.first_name}!`);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const fill = () => {};

  return (
    <div className="min-h-screen flex bg-background">
      {/* brand panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-[hsl(var(--sidebar))]">
        <img src="/login-bg.jpg" alt="MobileZone accessories" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--sidebar))] via-[hsl(var(--sidebar))]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img src="/mobilezone-logo.png" alt="MobileZone" className="h-full w-full object-contain" />
            </div>
            <div>
              <p className="font-heading font-bold text-lg">{t("appName")}</p>
              <p className="text-xs text-white/60 uppercase tracking-widest">{t("orgSuite")}</p>
            </div>
          </div>
          <div>
            <h1 className="font-heading font-bold text-4xl leading-tight tracking-tight">Run every<br />MobileZone branch<br /><span className="text-[hsl(217,91%,65%)]">from one place.</span></h1>
            <p className="text-white/70 mt-4 max-w-md text-sm">Manage branches, staff, roles and access for your phone &amp; accessories stores — securely, with a full activity trail.</p>
          </div>
          <div className="flex gap-6 text-xs text-white/50">
            <span>Charge</span><span>·</span><span>Protect</span><span>·</span><span>Connect</span>
          </div>
        </div>
      </div>

      {/* form panel */}
      <div className="flex-1 flex flex-col">
        <div className="flex justify-end items-center gap-2 p-5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5" data-testid="login-lang-btn">
                <Globe className="h-4 w-4" />{language === "en" ? "EN" : "SW"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLanguage("en")} data-testid="login-lang-en">English</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage("sw")} data-testid="login-lang-sw">Kiswahili</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="login-theme-btn">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 pb-10">
          <div className="w-full max-w-sm">
            <div className="lg:hidden flex items-center gap-2 mb-8">
              <div className="h-11 w-11 rounded-lg bg-white border border-border flex items-center justify-center overflow-hidden">
                <img src="/mobilezone-logo.png" alt="MobileZone" className="h-full w-full object-contain" />
              </div>
              <p className="font-heading font-bold text-lg">{t("appName")}</p>
            </div>
            <h2 className="text-2xl font-heading font-bold tracking-tight">{t("welcomeBack")}</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-6">{t("loginSubtitle")}</p>

            {error && <div className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">{t("email")}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  placeholder="you@organization.com" className="mt-1.5" data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password">{t("password")}</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  placeholder="••••••••" className="mt-1.5" data-testid="login-password-input" />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="login-remember" />
                  {t("rememberMe")}
                </label>
                <button type="button" className="text-sm text-primary hover:underline" onClick={() => setForgotOpen(true)} data-testid="login-forgot-btn">{t("forgotPassword")}</button>
              </div>
              <Button type="submit" className="w-full gap-2" disabled={loading} data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {loading ? t("signingIn") : t("signIn")}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <ForgotDialog open={forgotOpen} onClose={() => setForgotOpen(false)} t={t} />
    </div>
  );
}

function ForgotDialog({ open, onClose, t }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.demo_token) {
        toast.success("Reset link generated (demo). Redirecting...");
        window.location.href = `/reset-password?token=${data.demo_token}`;
      } else {
        toast.success(data.message);
        onClose();
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("forgotPassword")}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Enter your email to generate a password reset link. In this demo, the link is shown directly.</p>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email")} data-testid="forgot-email-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={submit} disabled={loading || !email} data-testid="forgot-submit-btn">Generate Link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
